import { useCallback, useEffect, useRef, useState } from 'react';
import type {
  GatewayPosition,
  GatewayBarData,
  PortfolioMetrics,
  ArsenalCombo,
  ComboStatus,
  StrategyState,
  ComboGenome,
  AlphaEvalReport,
  RdLoopState,
  JobStatus,
  AccountBalance,
  ExternalFeed,
  RegimeState,
  OrderData,
} from '../types/gateway';
import { useTerminalStore } from '../store/useTerminalStore';

export interface UseGatewayWSOptions {
  url?: string;
  autoConnect?: boolean;
  maxRetries?: number;
}

export interface UseGatewayWSReturn {
  status: 'CONNECTED' | 'RECONNECTING' | 'DISCONNECTED';
  send: (data: string) => void;
  connect: () => void;
  disconnect: () => void;
}

function buildWsUrl(): string {
  // Dev (Vite proxy on localhost:3000): falls through to window.location.host · proxy upgrades /ws → wss://api.trade-matrix-labs.tech
  // Prod (CF Pages): VITE_GATEWAY_WS_URL='wss://api.trade-matrix-labs.tech/ws' (set in CF Pages env vars)
  // Explicit env wins · else derive from VITE_API_BASE_URL (http→ws · https→wss) · else fall back to window.location.host
  const explicitWs = import.meta.env.VITE_GATEWAY_WS_URL as string | undefined;
  const apiBase = import.meta.env.VITE_API_BASE_URL as string | undefined;
  let base: string;
  if (explicitWs) {
    base = explicitWs;
  } else if (apiBase) {
    // Convert https://host → wss://host/ws · http://host → ws://host/ws
    base = apiBase.replace(/^http/, 'ws').replace(/\/+$/, '') + '/ws';
  } else {
    base = `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}/ws`;
  }
  const token = import.meta.env.VITE_GATEWAY_API_KEY as string | undefined;
  return token ? `${base}?token=${encodeURIComponent(token)}` : base;
}

const DEFAULT_URL = buildWsUrl();
const BASE_DELAY_MS = 1_000;
const MAX_DELAY_MS = 30_000;
const DEFAULT_MAX_RETRIES = 10;

function computeBackoff(attempt: number): number {
  return Math.min(BASE_DELAY_MS * Math.pow(2, attempt), MAX_DELAY_MS);
}

function parseWsFrame(raw: string): { type: string; data: unknown } | null {
  try {
    const parsed = JSON.parse(raw);
    if (typeof parsed === 'string') return { type: parsed, data: undefined };
    if (typeof parsed !== 'object' || parsed === null) return null;
    const obj = parsed as Record<string, unknown>;
    if (typeof obj['type'] === 'string') return { type: obj['type'] as string, data: obj['data'] };
    const keys = Object.keys(obj);
    if (keys.length >= 1) return { type: keys[0], data: obj[keys[0]] };
    return null;
  } catch {
    return null;
  }
}

export function useGatewayWS({
  url = DEFAULT_URL,
  autoConnect = true,
  maxRetries = DEFAULT_MAX_RETRIES,
}: UseGatewayWSOptions = {}): UseGatewayWSReturn {
  const [status, setStatus] = useState<'CONNECTED' | 'RECONNECTING' | 'DISCONNECTED'>(
    'DISCONNECTED',
  );

  const wsRef = useRef<WebSocket | null>(null);
  const retryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const retryCountRef = useRef(0);
  const manualDisconnectRef = useRef(false);

  const clearRetryTimer = useCallback(() => {
    if (retryTimerRef.current !== null) {
      clearTimeout(retryTimerRef.current);
      retryTimerRef.current = null;
    }
  }, []);

  const handleMessage = useCallback((event: MessageEvent) => {
    const msg = parseWsFrame(event.data as string);
    if (!msg) return;

    const store = useTerminalStore.getState();

    switch (msg.type) {
      case 'PositionUpdate':
        store.setPositions(
          (msg.data as GatewayPosition[]).map((p) => {
            const sign = p.side === 'Sell' ? -1 : 1;
            const pnl = p.unrealized_pnl !== 0
              ? p.unrealized_pnl
              : (p.current_price - p.entry_price) * sign * p.quantity;
            return {
              symbol: p.symbol,
              qty: sign * p.quantity,
              entry_price: p.entry_price,
              current_price: p.current_price,
              unrealized_pnl: pnl,
              view: 'HEDGING' as const,
            };
          }),
        );
        break;

      case 'ArsenalUpdate':
        for (const combo of msg.data as ArsenalCombo[]) {
          store.updateCombo({
            combo: combo.name,
            win_rate: combo.win_rate,
            pnl: 0,
            weight: 1 / (msg.data as ArsenalCombo[]).length,
            cusum_score: combo.sharpe,
            enabled: combo.status === 'active',
          });
        }
        break;

      case 'BarClose': {
        const raw = msg.data as GatewayBarData;
        const barSymbol = raw.symbol || 'BTCUSDT';
        if (barSymbol !== store.selectedSymbol) break;
        store.addBar({
          time: new Date(raw.bar_close_time).getTime() / 1000,
          open: raw.open,
          high: raw.high,
          low: raw.low,
          close: raw.close,
          volume: raw.volume,
        });
        break;
      }

      case 'ComboStatus':
        store.updateCombo(msg.data as ComboStatus);
        break;

      case 'KillSwitchAck': {
        const ksData = msg.data as { armed: boolean };
        store.setKillSwitch(ksData.armed);
        store.addToast(ksData.armed ? 'warning' : 'success', ksData.armed ? 'Kill switch ARMED — all positions at risk' : 'Kill switch disarmed — trading resumed');
        break;
      }

      case 'ComboWeightAck': {
        const ack = msg.data as { combo: string; weight: number };
        store.updateComboWeight(ack.combo, ack.weight);
        store.addWeightChange(ack.combo, ack.weight);
        break;
      }

      case 'StrategyState':
        store.setStrategyState(msg.data as StrategyState);
        break;

      case 'ComboGenome':
        store.setGenomes([msg.data as ComboGenome]);
        break;

      case 'AlphaEval':
        store.setAlphaEval(msg.data as AlphaEvalReport);
        break;

      case 'MetricsUpdate':
        store.setMetrics(msg.data as PortfolioMetrics);
        break;

      case 'RdLoopUpdate':
        store.setRdLoopState(msg.data as RdLoopState);
        break;

      case 'JobUpdate':
        store.setJobs(msg.data as JobStatus[]);
        break;

      case 'AccountUpdate':
        store.setAccountBalance(msg.data as AccountBalance);
        break;

      case 'ExternalUpdate':
        store.setExternalFeeds(msg.data as ExternalFeed[]);
        break;

      case 'RegimeUpdate':
        store.setRegimeState(msg.data as RegimeState);
        break;

      case 'OrderUpdate': {
        const order = msg.data as OrderData;
        const currentOrders = store.orders ?? [];
        const idx = currentOrders.findIndex((o) => o.id === order.id);
        if (idx >= 0) {
          const updated = [...currentOrders];
          updated[idx] = order;
          store.setOrders(updated);
        } else {
          store.setOrders([...currentOrders, order]);
        }
        break;
      }

      case 'OrderbookUpdate': {
        const raw = msg.data as {
          symbol: string;
          bids: Array<{ price: number; size?: number; qty?: number } | [number, number]>;
          asks: Array<{ price: number; size?: number; qty?: number } | [number, number]>;
          timestamp?: number;
        };
        if (raw.symbol !== store.selectedSymbol) break;
        const parseLevel = (level: { price: number; size?: number; qty?: number } | [number, number]) =>
          Array.isArray(level)
            ? { price: level[0], size: level[1] }
            : { price: level.price, size: level.size ?? level.qty ?? 0 };
        store.setOrderbook({
          symbol: raw.symbol,
          bids: raw.bids.map(parseLevel),
          asks: raw.asks.map(parseLevel),
          timestamp: typeof raw.timestamp === 'number' ? raw.timestamp : undefined,
        });
        break;
      }

      case 'KillSwitchEvent': {
        const ksEvent = msg.data as { action?: unknown; timestamp?: string };
        if (ksEvent.action === 'arm' || ksEvent.action === 'resume') {
          store.setKillSwitch(true);
        } else if (ksEvent.action === 'kill') {
          store.setKillSwitch(false);
          store.addToast('error', 'Kill switch ACTIVATED — all positions liquidating');
        }
        if (ksEvent.action === 'resume') {
          store.addToast('success', 'Kill switch resumed — trading active');
        }
        break;
      }

      case 'LogLine':
      case 'Heartbeat':
        break;
    }
  }, []);

  const connectRef = useRef<() => void>(() => undefined);

  const connect = useCallback(() => {
    clearRetryTimer();
    manualDisconnectRef.current = false;

    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.close();
      wsRef.current = null;
    }

    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      retryCountRef.current = 0;
      setStatus('CONNECTED');
      useTerminalStore.getState().setWsStatus('CONNECTED');
    };

    ws.onmessage = handleMessage;

    ws.onclose = () => {
      if (manualDisconnectRef.current) {
        setStatus('DISCONNECTED');
        useTerminalStore.getState().setWsStatus('DISCONNECTED');
        return;
      }

      if (retryCountRef.current >= maxRetries) {
        setStatus('DISCONNECTED');
        useTerminalStore.getState().setWsStatus('DISCONNECTED');
        return;
      }

      setStatus('RECONNECTING');
      useTerminalStore.getState().setWsStatus('RECONNECTING');

      const delay = computeBackoff(retryCountRef.current);
      retryCountRef.current += 1;

      retryTimerRef.current = setTimeout(() => {
        connectRef.current();
      }, delay);
    };

    ws.onerror = () => {};
  }, [url, maxRetries, clearRetryTimer, handleMessage]);

  useEffect(() => {
    connectRef.current = connect;
  }, [connect]);

  const disconnect = useCallback(() => {
    manualDisconnectRef.current = true;
    clearRetryTimer();
    if (wsRef.current) {
      wsRef.current.onopen = null;
      wsRef.current.onclose = null;
      wsRef.current.onerror = null;
      wsRef.current.onmessage = null;
      wsRef.current.close();
      wsRef.current = null;
    }
    setStatus('DISCONNECTED');
    useTerminalStore.getState().setWsStatus('DISCONNECTED');
  }, [clearRetryTimer]);

  const send = useCallback((data: string) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(data);
    }
  }, []);

  useEffect(() => {
    if (autoConnect) {
      connect();
      return () => disconnect();
    }

    disconnect();
    return undefined;
  }, [autoConnect, connect, disconnect]);

  return { status, send, connect, disconnect };
}
